import {
  createId,
  docMmToPx,
  docPxToMm,
  GUIDE_LIMIT,
  type Guide,
  type LayoutGrid,
  type SceneDocument,
} from "@cva/shared";
import { create } from "zustand";
import type { EditorStore } from "./store";

/**
 * View settings of the design editor (rulers, guides, layout grid). They are
 * the viewer's own choice, not part of the document, and remembered in this
 * browser. The guides and the grid themselves belong to the document.
 */
export interface ViewPrefs {
  rulers: boolean;
  guides: boolean;
  /** Guides cannot be moved or deleted (new ones can still be pulled out). */
  guidesLocked: boolean;
  grid: boolean;
}

const STORAGE_KEY = "cva.editor.view";
const DEFAULTS: ViewPrefs = { rulers: true, guides: true, guidesLocked: false, grid: true };

function load(): ViewPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Record<keyof ViewPrefs, unknown>>;
    const pick = (key: keyof ViewPrefs) =>
      typeof parsed[key] === "boolean" ? (parsed[key] as boolean) : DEFAULTS[key];
    return {
      rulers: pick("rulers"),
      guides: pick("guides"),
      guidesLocked: pick("guidesLocked"),
      grid: pick("grid"),
    };
  } catch {
    return DEFAULTS;
  }
}

export const useViewPrefs = create<
  ViewPrefs & {
    setPrefs(patch: Partial<ViewPrefs>): void;
    toggle(key: keyof ViewPrefs): void;
  }
>()((set, get) => ({
  ...load(),
  setPrefs(patch) {
    set(patch);
    const { rulers, guides, guidesLocked, grid } = get();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ rulers, guides, guidesLocked, grid }));
    } catch {
      // Private mode or blocked storage: the setting lasts until the tab closes.
    }
  },
  toggle(key) {
    get().setPrefs({ [key]: !get()[key] });
  },
}));

// Other tabs of this browser follow a changed setting.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) useViewPrefs.setState(load());
  });
}

// ---------------------------------------------------------------------------
// Units: mm for print documents, px otherwise
// ---------------------------------------------------------------------------

export function layoutUnits(doc: SceneDocument) {
  const print = Boolean(doc.format.print);
  return {
    print,
    unit: print ? "mm" : "px",
    precision: print ? 1 : 0,
    step: print ? 0.5 : 1,
    toDisplay: (px: number) => (print ? docPxToMm(doc, px) : px),
    fromDisplay: (value: number) => (print ? docMmToPx(doc, value) : value),
  };
}

/** "12,5 mm" or "340 px". */
export function formatLength(doc: SceneDocument, px: number): string {
  const units = layoutUnits(doc);
  const value = Number(units.toDisplay(px).toFixed(units.precision));
  return `${value.toLocaleString("de-DE")} ${units.unit}`;
}

/** Rounds a guide position to 0.1 mm (print) or whole pixels. */
export function roundGuidePosition(doc: SceneDocument, px: number): number {
  const units = layoutUnits(doc);
  if (!units.print) return Math.round(px);
  const mm = Math.round(units.toDisplay(px) * 10) / 10;
  return Math.round(units.fromDisplay(mm) * 1000) / 1000;
}

// ---------------------------------------------------------------------------
// Ruler ticks
// ---------------------------------------------------------------------------

/** Labelled tick spacing (in display units) and the number of minor ticks between them. */
export function rulerTicks(pxPerUnit: number, zoom: number): { step: number; minor: number } {
  const screenPerUnit = pxPerUnit * zoom;
  let step = 1;
  let multiple = 1;
  search: for (let exponent = -1; exponent <= 7; exponent++) {
    for (const m of [1, 2, 5]) {
      const candidate = m * 10 ** exponent;
      if (candidate * screenPerUnit >= 56) {
        step = candidate;
        multiple = m;
        break search;
      }
    }
  }
  const options = multiple === 1 ? [10, 5, 2] : multiple === 2 ? [4, 2] : [5];
  const minor = options.find((count) => (step * screenPerUnit) / count >= 5) ?? 1;
  return { step, minor };
}

export function formatTick(value: number, step: number): string {
  const decimals = step < 1 ? 1 : 0;
  const rounded = Number(value.toFixed(decimals));
  return (Object.is(rounded, -0) ? 0 : rounded).toLocaleString("de-DE", { useGrouping: false });
}

// ---------------------------------------------------------------------------
// Guide and grid commands (each one undo step, shared live like any edit)
// ---------------------------------------------------------------------------

export function addGuide(store: EditorStore, axis: Guide["axis"], position: number): string | null {
  const state = store.getState();
  if (state.readOnly || (state.doc.guides?.length ?? 0) >= GUIDE_LIMIT) return null;
  const guide: Guide = { id: createId("guide"), axis, position };
  state.update((draft) => {
    if (draft.guides) draft.guides.push(guide);
    else draft.guides = [guide];
  });
  if (!useViewPrefs.getState().guides) useViewPrefs.getState().setPrefs({ guides: true });
  return guide.id;
}

export function moveGuide(store: EditorStore, id: string, position: number) {
  store.getState().update((draft) => {
    const guide = draft.guides?.find((item) => item.id === id);
    if (guide) guide.position = position;
  });
}

export function removeGuide(store: EditorStore, id: string) {
  store.getState().update((draft) => {
    const index = draft.guides?.findIndex((item) => item.id === id) ?? -1;
    if (index >= 0) draft.guides!.splice(index, 1);
  });
}

export function clearGuides(store: EditorStore) {
  store.getState().update((draft) => {
    // An empty list rather than no list: concurrent additions of others survive.
    if (draft.guides?.length) draft.guides.splice(0, draft.guides.length);
  });
}

export function setLayoutGrid(
  store: EditorStore,
  grid: LayoutGrid | null,
  options?: { merge?: string },
) {
  store.getState().update((draft) => {
    if (grid) draft.layoutGrid = grid;
    else delete draft.layoutGrid;
  }, options);
  if (grid && !useViewPrefs.getState().grid) useViewPrefs.getState().setPrefs({ grid: true });
}
